import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const AuthCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const token = searchParams.get('token');
  const error = searchParams.get('error');
  const [showRegistrationClosed, setShowRegistrationClosed] = useState(false);
  
  // Prevent double execution (React StrictMode or re-renders)
  const processedRef = useRef(false);

  useEffect(() => {
    // Guard against double execution
    if (processedRef.current) return;
    processedRef.current = true;
    
    if (token) {
      if (loginWithToken) {
          loginWithToken(token);
          navigate('/');
      } else {
          // Fallback if loginWithToken is not available context-wise yet
          localStorage.setItem('token', token);
           // Force reload or redirect to allow AuthContext to pick it up on mount
          window.location.href = '/';
      }
    } else if (error) {
      // Check if it's a registration closed error
      if (error.toLowerCase().includes('registration') && error.toLowerCase().includes('closed')) {
        setShowRegistrationClosed(true);
      } else {
        // Handle other error cases
        navigate('/login?error=' + error);
      }
    } else {
      navigate('/login');
    }
  }, [token, error, navigate, loginWithToken]);

  // Show Registration Closed UI
  if (showRegistrationClosed) {
    return (
      <div className="min-h-screen w-full flex bg-gray-50">
        {/* Left Side - Decorative Neo */}
        <div className="hidden lg:flex w-1/2 bg-[#161E54] relative overflow-hidden items-center justify-center p-12 border-r-4 border-black">
          <div className="absolute inset-0 z-0 opacity-10">
            <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_50%,#F16D34_2px,transparent_2px)] bg-[size:24px_24px]" />
          </div>

          <div className="relative z-10 max-w-lg text-[#F7F7F7] space-y-8">
            <h1 className="text-6xl font-black tracking-tighter leading-tight text-[#BBE0EF]">
              OOPS!<br/>
              NOT YET.
            </h1>
            
            <p className="text-xl text-[#FF986A] font-medium max-w-md">
              We're not accepting new registrations at this time. Check back soon!
            </p>

            <div className="grid grid-cols-2 gap-6 pt-4">
               <div className="p-6 bg-[#BBE0EF] border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] text-[#161E54]">
                  <div className="text-4xl font-black mb-1">🔒</div>
                  <div className="text-sm font-bold uppercase tracking-wide">Access Limited</div>
               </div>
               <div className="p-6 bg-[#F16D34] border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] text-white">
                  <div className="text-4xl font-black mb-1">⏳</div>
                  <div className="text-sm font-bold uppercase tracking-wide">Coming Soon</div>
               </div>
            </div>
          </div>
        </div>

        {/* Right Side - Message */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-4 sm:p-12 relative bg-[#F7F7F7]">
          <div className="w-full max-w-[480px] relative z-10">
            <div className="bg-white border-4 border-[#161E54] p-8 sm:p-12 shadow-neo lg:shadow-neo-lg">
              {/* Google Icon with Lock */}
              <div className="mb-8 text-center">
                <div className="relative inline-block">
                  <div className="w-24 h-24 bg-white border-4 border-black rounded-2xl flex items-center justify-center mx-auto shadow-neo">
                    <svg className="w-12 h-12" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  </div>
                  {/* Lock badge */}
                  <div className="absolute -top-2 -right-2 w-10 h-10 bg-red-500 rounded-full border-3 border-black flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="text-center mb-8">
                <h2 className="text-3xl sm:text-4xl font-black text-[#161E54] mb-4 uppercase tracking-tight">
                  Registration Closed
                </h2>
                <div className="h-2 w-20 bg-[#F16D34] mx-auto mb-6" />
                <p className="text-[#161E54] font-medium text-lg leading-relaxed">
                  We're not accepting new users via Google Sign-in at this time. If you already have an account, try signing in with your email.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="space-y-4">
                <Link 
                  to="/login"
                  className="w-full h-14 bg-[#F16D34] hover:bg-[#E05D25] text-white font-black text-lg border-4 border-black shadow-neo hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all flex items-center justify-center gap-2"
                >
                  SIGN IN WITH EMAIL
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </Link>

                <Link 
                  to="/"
                  className="w-full h-12 bg-white hover:bg-[#F7F7F7] text-[#161E54] font-bold border-4 border-[#161E54] shadow-neo-sm hover:shadow-none hover:translate-x-0.5 hover:translate-y-0.5 transition-all flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                  Back to Home
                </Link>
              </div>

              {/* Contact Section */}
              <div className="mt-8 text-center bg-[#BBE0EF] border-2 border-[#161E54] p-4 shadow-neo-sm">
                <p className="text-sm text-[#161E54] font-bold">
                  Have an invite code?{' '}
                  <Link 
                    to="/register" 
                    className="font-black text-[#F16D34] hover:underline decoration-2 underline-offset-2"
                  >
                    Register Here
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        <p className="mt-4 text-gray-600">Completing sign in...</p>
      </div>
    </div>
  );
};

export default AuthCallbackPage;

